import fetcher from '../fetcher';
import { encryptData, importPublicKey } from '@/utils/encryption';
import { setCookie } from '@/utils/cookie';
import { setSentryUser, clearSentryUser } from '../../sentry';

// Helper function to save user info to sessionStorage
const saveUserInfo = (userData: {
	id: string;
	email: string;
	username: string;
	role: string;
	fullName?: string;
	groupName?: string;
}) => {
	const userInfo = {
		id: userData.id,
		email: userData.email,
		username: userData.username,
		role: userData.role,
		name: userData.fullName || userData.username,
		groupName: userData.groupName,
	};
	sessionStorage.setItem('fleet-user-info', JSON.stringify(userInfo));
};

const { VITE_FLEET_PUBLIC_KEY } = import.meta.env;

export type AuthData = {
	id: string;
	email: string;
	username: string;
	jwtToken: string;
	refreshToken: string;
	role: 'User' | 'Admin';
};

const storeAuthData = async (data: AuthData) => {
	const publicKey = await importPublicKey(VITE_FLEET_PUBLIC_KEY);
	const refreshToken = await encryptData(data.refreshToken, publicKey);
	const accessToken = await encryptData(data.jwtToken, publicKey);
	// const accessTokenMaxAge = parseJWT(data.jwtToken).exp - dayjs().unix() - 60;

	setCookie([
		{
			name: 'FLTAT',
			value: accessToken,
			maxAge: 3600 - 300,
		},
		{
			name: 'FLTRT',
			value: refreshToken,
			maxAge: 86400 - 300,
		},
	]);

	localStorage.setItem('role', data.role);
};

export type AuthPayload = {
	userName: string;
	password: string;
};

export const authenticate = async (payload: AuthPayload) => {
	const { data } = await fetcher<AuthData>({
		api: '/users/authenticate',
		handleAuth: false,
		handleError: false,
		body: payload,
	});

	await storeAuthData(data);

	// Save user info to sessionStorage for debug panel
	saveUserInfo({
		id: data.id,
		email: data.email,
		username: data.username,
		role: data.role,
	});

	// Set user context for error tracking
	setSentryUser({
		id: data.id,
		email: data.email,
		role: data.role,
	});

	return data;
};

export const unauthenticate = async ({ logout = true }) => {
	// Clear user info from sessionStorage
	sessionStorage.removeItem('fleet-user-info');
	sessionStorage.clear();

	if (logout) {
		try {
			await fetcher<AuthData>({
				api: '/users/logout',
				handleAuth: false,
			});
		} catch (error) {
			console.error(error);
			// window.location.reload();
		} finally {
			setTimeout(() => {
				window.location.reload();
			}, 10);
		}
		// window.location.reload();
	}

	// Clear user context from error tracking
	clearSentryUser();

	localStorage.removeItem('role');

	setCookie([
		{
			name: 'FLTAT',
			value: '',
			maxAge: 0,
		},
		{
			name: 'FLTRT',
			value: '',
			maxAge: 0,
		},
	]);

	// Force navigation to login page after logout
	// window.location.reload();
};

export const authRefresh = async (token: string) => {
	const { data } = await fetcher<AuthData>({
		api: '/users/refresh-token',
		body: { token },
		handleAuth: false,
	});

	await storeAuthData(data);
	return data;
};

export const forgotPassword = async (email: string) => {
	return fetcher({
		api: '/users/forgot-password',
		body: { email },
	});
};

export type UserGroupName =
	| 'FRM'
	| 'FBDH'
	| 'CCA'
	| 'CCDH'
	| 'FH'
	| 'FS'
	| 'CH'
	| 'FCOH'
	| 'FCOM'
	| 'FCDVS'
	| 'FCAS'
	| 'SECOM'
	| 'FCS'
	| 'BOD'
	| 'CR'
	| 'ADMIN';

export type SimpleUserData = {
	userId: string;
	userName: string;
	fullName: string;
	role: string;
	groupName: UserGroupName;
};

export type UserData = SimpleUserData & {
	email: string;
	isActive: boolean;
	menus: {
		id: string;
		title: string;
		url: string;
		isActiveMenu: boolean;
		isAccessible: boolean;
	}[];
};

export const getUserData = async () => {
	const result = await fetcher<UserData>({
		api: '/users/me',
		method: 'POST',
	});

	// Save complete user info to sessionStorage for debug panel
	saveUserInfo({
		id: result.data.userId,
		email: result.data.email,
		username: result.data.userName,
		role: result.data.role,
		fullName: result.data.fullName,
		groupName: result.data.groupName,
	});

	return result;
};

export const getUsers = () => {
	return fetcher<UserData[]>({
		api: '/admin/users/by-role',
		body: { roleId: 1 },
	});
};

export type OrganisationStructurePayload = {
	supervisor: SimpleUserData;
	subordinates: SimpleUserData[];
};

export const getOrganisationStructure = () => {
	return fetcher<OrganisationStructurePayload>({
		api: '/users/get-organisation-structure',
	});
};

export type GetUserListPayload = {
	role: UserGroupName;
	reportToId?: number;
	divisionId?: number;
};

/** @deprecated use v2GetUserLists or contact backend for admin-specific alternative */
export const getUserLists = (body: GetUserListPayload) => {
	return fetcher<SimpleUserData[]>({
		api: '/users/get-user-options',
		body,
	});
};

export const v2GetUserLists = (body: GetUserListPayload) => {
	return fetcher<SimpleUserData[]>({
		api: '/users/v2/get-user-options',
		body,
	});
};
